import { Box, Button, Typography, useTheme } from '@mui/material';

import { useCommonStyles } from '../../styles/GenericStyle';
import { MtComStyleObj } from '../../styles/MtComStyleObj';
import GenericFilterPanel from './GenericFilterPanel';
import ShowFilterChips from './ShowFilterChips';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import FilterButton from './FilterButton';
export interface DashboardHeaderProps {
    title: string;
    accessibleFilters: string[];
    selectedFilter: string;
    onFilterChange: (filter: string) => void;
    isFilterVisible: boolean;
    setFilterVisible: (visible: boolean | ((prev: boolean) => boolean)) => void;
    showFilters?: boolean;
    setFilterFieldData?: (data: any) => void;
    filterValues?: any[];
    // Filter panel props
    filterPanelProps?: {
        moduleName: string;
        listValues: any;
        setGridData: (data: any) => void;
        showArchived: boolean;
        onRefreshData: () => void;
        setTotalRows?: (rows: number) => void;
        paginationModel?: any;
        onFilterApplied?: (data: any | null) => void;
        accessibleFilterTab?: string;
        persistState?: boolean;
        extraBody?: Record<string, any>;
        searchTrigger?: number;
    };
    // Optional slots for additional content
    searchSlot?: ReactNode;
    actionButtonsSlot?: ReactNode;
    isDashboardHeaderVisiBle: boolean;
    kpiToggleButton?: ReactNode;
    kpiContent?: ReactNode;
}

export const DashboardHeader = ({
    title,
    accessibleFilters,
    selectedFilter,
    onFilterChange,
    isFilterVisible,
    setFilterVisible,
    showFilters = true,
    setFilterFieldData,
    filterValues,
    filterPanelProps,
    searchSlot,
    actionButtonsSlot,
    isDashboardHeaderVisiBle,
    kpiToggleButton,
    kpiContent
}: DashboardHeaderProps) => {
    const classes = useCommonStyles();
    const theme = useTheme();
    const styles = MtComStyleObj(theme);
    const [filterDel, setFilterDel] = useState<Record<string, any>>({});

    const handleDeleteFilter = useCallback((filter: any) => {

        setFilterDel(filter);
    }, []);

    useEffect(() => {
        if (!filterDel?.name) return;

        const timer = window.setTimeout(() => setFilterDel({}), 0);
        return () => window.clearTimeout(timer);
    }, [filterDel]);

    const hasSecondRowContent = !!searchSlot || !!actionButtonsSlot;
    const hasFilterChips = Array.isArray(filterValues) && filterValues.length > 0;
    // Accessible-filter pill highlight reflects the actively selected filter. Some
    // accessible filters (e.g. "High priority MatIDs" on the trials dashboard) do
    // not produce filter chips, so we must NOT gate the highlight on chip presence.
    // In archived view: actionButtonsSlot is absent but searchSlot is present — collapse into title row
    const collapseSearchIntoTitleRow = !!searchSlot && !actionButtonsSlot;
    // When no search/action slots exist and filters are shown (non-archived), push filter button to second row
    const filterOnSecondRow = !hasSecondRowContent && showFilters;

    return (
        <>
            <Box sx={{ ...styles.dashboardHeaderWrapper, display: isDashboardHeaderVisiBle ? '' : 'none' }}>
                <Box sx={styles.dashboardHeaderRow}>
                    <Box sx={styles.dashboardHeaderLeftCol}>
                        <Box sx={styles.dashboardTitleRow}>
                            <Typography
                                component="span"
                                className={classes.labelNoto28Bold600}
                                sx={styles.dashboardTitle}
                            >
                                {title}
                            </Typography>
                            {showFilters && accessibleFilters.map((filter: string) => (
                                <Button
                                    key={filter}
                                    variant={selectedFilter === filter ? "contained" : "outlined"}
                                    color="primary"
                                    size="small"
                                    disableRipple

                                    className={selectedFilter === filter ? classes.selectedChipFilterButton : classes.chipFilterButton}
                                    sx={styles.dashboardFilterChip}
                                    onClick={() => onFilterChange(filter)}
                                >
                                    <Typography component="span" className={classes.chipFilterButtonText}>{filter}</Typography>
                                </Button>
                            ))}
                        </Box>
                        {kpiToggleButton && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {kpiToggleButton}
                            </Box>
                        )}
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        ...styles.dashboardActionButtons,
                    }}>
                        {actionButtonsSlot}
                        {collapseSearchIntoTitleRow && (
                            <Box sx={styles.dashboardSearchRow}>
                                {searchSlot}
                                <FilterButton isFilterVisible={isFilterVisible} setFilterVisible={setFilterVisible} />
                            </Box>
                        )}
                        {!hasSecondRowContent && !filterOnSecondRow && (
                            <FilterButton isFilterVisible={isFilterVisible} setFilterVisible={setFilterVisible} />
                        )}
                    </Box>
                </Box>

                {kpiContent}

                {(hasSecondRowContent && !collapseSearchIntoTitleRow) || hasFilterChips || filterOnSecondRow ? (
                    <Box sx={{
                        ...styles.dashboardFilterRow,
                        justifyContent: hasFilterChips ? 'space-between' : 'flex-end',
                    }}>
                        <Box sx={styles.dashboardChipsWrapper}>
                            {hasFilterChips ? (
                                <ShowFilterChips filterValues={filterValues} onDelete={handleDeleteFilter} />
                            ) : null}
                        </Box>
                        {!collapseSearchIntoTitleRow && (
                            <Box sx={styles.dashboardSearchRow}>
                                {searchSlot}
                                {(hasSecondRowContent || filterOnSecondRow) && (
                                    <FilterButton isFilterVisible={isFilterVisible} setFilterVisible={setFilterVisible} />
                                )}
                            </Box>
                        )}
                    </Box>
                ) : hasFilterChips ? (
                    <Box sx={{
                        ...styles.dashboardFilterRow,
                        justifyContent: 'flex-start',
                    }}>
                        <Box sx={styles.dashboardChipsWrapper}>
                            <ShowFilterChips filterValues={filterValues} onDelete={handleDeleteFilter} />
                        </Box>
                    </Box>
                ) : null}


                <GenericFilterPanel
                    key={`${filterPanelProps!.moduleName}-${filterPanelProps!.showArchived ? 'archived' : 'current'}`}
                    moduleName={filterPanelProps!.moduleName}
                    listValues={filterPanelProps!.listValues}
                    setGridData={filterPanelProps!.setGridData}
                    showArchived={filterPanelProps!.showArchived ?? false}
                    onRefreshData={filterPanelProps!.onRefreshData}
                    setTotalRows={filterPanelProps!.setTotalRows}
                    paginationModel={filterPanelProps!.paginationModel}
                    onFilterApplied={filterPanelProps!.onFilterApplied}
                    isFilterVisible={isFilterVisible}
                    setFilterVisible={setFilterVisible}
                    setFilterFieldData={setFilterFieldData!}
                    accessibleFilterTab={filterPanelProps!.accessibleFilterTab ?? ''}
                    persistState={filterPanelProps!.persistState}
                    extraBody={filterPanelProps!.extraBody}
                    searchTrigger={filterPanelProps!.searchTrigger}
                    filtetToDelete={filterDel}
                />
            </Box>

        </>
    );
};

export default DashboardHeader;
